import React, { useRef } from 'react';
import { ArrowRight, Printer } from 'lucide-react';
import { Button } from './ui/button';

export const C = {
  escuro:   '#1E1B4B',
  primario: '#5B21B6',
  medio:    '#7C3AED',
  menta:    '#C4B5FD',
  creme:    '#F8F7FF',
  cremelc:  '#F3F0FF',
  cinza:    '#6B7280',
  borda:    '#DDD6FE',
  roxo:     '#534AB7',
  teal:     '#0F6E56',
  coral:    '#C5513A',
};

const nivelDominante = (media) => {
  if (media <= 1.7) return 'Em desenvolvimento inicial';
  if (media <= 2.5) return 'Em desenvolvimento';
  if (media <= 3.2) return 'Em expansão';
  return 'Plenamente desenvolvida';
};

const FORBIDDEN = {
  'Inabilidoso': 'Em desenvolvimento inicial', 'inabilidoso': 'em desenvolvimento inicial',
  'Despertando': 'Em desenvolvimento',         'despertando': 'em desenvolvimento',
  'Capaz': 'Disponível',                       'capaz': 'disponível',
  'Integrado': 'Plenamente desenvolvida',      'integrado': 'plenamente desenvolvida',
  'integrada': 'plenamente desenvolvida',
};
const sanitize = (t = '') => Object.entries(FORBIDDEN).reduce((s, [k, v]) => s.replaceAll(k, v), t);

const HabilidadeCard = ({ habilidade, descricao, borderColor }) => (
  <div className="rounded-lg p-4 bg-white border" style={{ borderLeft: `4px solid ${borderColor}`, borderTop: `1px solid ${C.borda}`, borderRight: `1px solid ${C.borda}`, borderBottom: `1px solid ${C.borda}` }}>
    <p className="font-bold text-gray-900 mb-1" style={{ fontFamily: 'Georgia, serif', fontSize: 15 }}>
      {habilidade}
    </p>
    <p className="text-sm leading-relaxed" style={{ color: C.cinza }}>
      {sanitize(descricao)}
    </p>
  </div>
);

const FocoBlock = ({ numero, titulo, habilidades = [], descricao, headerColor }) => (
  <div className="rounded-xl overflow-hidden border" style={{ borderColor: C.borda }}>
    <div className="px-5 py-4 flex items-center gap-3" style={{ backgroundColor: headerColor }}>
      <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
        <span className="text-white font-bold text-sm">{numero}</span>
      </div>
      <p className="font-bold text-white text-base">{titulo}</p>
    </div>
    <div className="px-5 py-4" style={{ backgroundColor: C.cremelc }}>
      {habilidades.length > 0 && (
        <p className="text-sm mb-2" style={{ color: C.cinza }}>
          {habilidades.map((h, i) => (
            <span key={`hab-${i}`}><em>{sanitize(h)}</em>{i < habilidades.length - 1 ? ' · ' : ''}</span>
          ))}
        </p>
      )}
      <p className="text-sm leading-relaxed text-gray-800">{sanitize(descricao)}</p>
    </div>
  </div>
);

const Relatorio = ({ result, nome, intencao, onContinue, isSaving }) => {
  const reportRef = useRef();
  const { forcas = [], desenvolvimento = [], tensoes = [], plano = [], perfil_geral = '' } = result;
  const dominios = result.dominios || {};
  const mediaGlobal = Object.values(dominios).length
    ? Object.values(dominios).reduce((s, d) => s + (d.media || 0), 0) / Object.values(dominios).length
    : 2.5;
  const nivel = nivelDominante(mediaGlobal);
  const dataFormatada = new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });

  const handlePrint = () => window.print();

  return (
    <div>
      {/* Botão PDF */}
      <div className="flex justify-end mb-4 print:hidden">
        <button
          onClick={handlePrint}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white"
          style={{ backgroundColor: C.medio }}
        >
          <Printer className="w-4 h-4" />
          Salvar PDF
        </button>
      </div>

      <div ref={reportRef} className="space-y-0 rounded-2xl overflow-hidden border" style={{ borderColor: C.borda }}>

        {/* SEÇÃO 1 — Capa */}
        <div className="flex" style={{ backgroundColor: C.escuro }}>
          <div className="px-6 py-8 flex flex-col justify-between" style={{ backgroundColor: C.primario, minWidth: 140 }}>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: C.menta }}>
                Inteligência Relacional
              </p>
              <p className="text-2xl font-extrabold text-white tracking-tight">simby</p>
            </div>
            <p className="text-xs mt-8" style={{ color: C.menta }}>Plataforma de<br />Desenvolvimento Relacional</p>
          </div>
          <div className="px-8 py-8 flex-1">
            <p className="text-xs uppercase tracking-widest mb-2" style={{ color: C.menta }}>Perfil Relacional</p>
            <h1 className="text-2xl font-bold text-white leading-snug mb-2">{nome}</h1>
            <p className="text-sm mb-1" style={{ color: C.menta }}>Análise de habilidades relacionais</p>
            <p className="text-xs" style={{ color: C.menta }}>{dataFormatada}</p>
          </div>
        </div>

        {/* SEÇÃO 2 — Visão geral */}
        <div className="px-6 py-7" style={{ backgroundColor: C.creme }}>
          <p className="text-lg font-bold mb-5" style={{ color: C.escuro }}>Visão geral do seu perfil</p>
          <div className="grid grid-cols-3 gap-3 mb-5">
            {[
              { label: 'Nível dominante', value: nivel },
              { label: 'Habilidades mapeadas', value: '28' },
              { label: 'Tensões identificadas', value: tensoes.length.toString() },
            ].map(({ label, value }) => (
              <div key={label} className="rounded-xl p-4 text-center" style={{ backgroundColor: C.cremelc, border: `1px solid ${C.borda}` }}>
                <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: C.cinza }}>{label}</p>
                <p className="font-bold text-base" style={{ color: C.escuro }}>{value}</p>
              </div>
            ))}
          </div>
          {perfil_geral && (
            <div className="rounded-xl p-5" style={{ backgroundColor: C.escuro }}>
              <p className="text-sm leading-relaxed" style={{ color: C.menta }}>{sanitize(perfil_geral)}</p>
            </div>
          )}
        </div>

        {/* SEÇÃO 3 — Pontos de força */}
        {forcas.length > 0 && (
          <div className="px-6 py-7 border-t" style={{ borderColor: C.borda }}>
            <p className="text-lg font-bold mb-1" style={{ color: C.escuro }}>Seus pontos de força</p>
            <p className="text-sm mb-5" style={{ color: C.cinza }}>Recursos disponíveis agora.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {forcas.map((f, i) => (
                <HabilidadeCard
                  key={`forca-${i}`}
                  habilidade={sanitize(f.habilidade)}
                  descricao={f.descricao}
                  borderColor={f.nivel === 'Integrado' || f.nivel?.includes('Plenamente') ? C.roxo : C.teal}
                />
              ))}
            </div>
          </div>
        )}

        {/* SEÇÃO 4 — Zonas de desenvolvimento */}
        {desenvolvimento.length > 0 && (
          <div className="px-6 py-7 border-t" style={{ borderColor: C.borda }}>
            <p className="text-lg font-bold mb-1" style={{ color: C.escuro }}>Zonas de desenvolvimento</p>
            <p className="text-sm mb-5" style={{ color: C.cinza }}>Oportunidades de crescimento.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {desenvolvimento.map((d, i) => (
                <HabilidadeCard
                  key={`dev-${i}`}
                  habilidade={sanitize(d.habilidade)}
                  descricao={d.descricao}
                  borderColor={C.coral}
                />
              ))}
            </div>
          </div>
        )}

        {/* SEÇÃO 5 — Tensões internas */}
        {tensoes.length > 0 && (
          <div className="px-6 py-7 border-t" style={{ borderColor: C.borda }}>
            <p className="text-lg font-bold mb-1" style={{ color: C.escuro }}>Tensões internas</p>
            <p className="text-sm mb-5" style={{ color: C.cinza }}>O que merece atenção especial.</p>
            <div className="space-y-3">
              {tensoes.map((t, i) => (
                <div key={`tensao-${i}`} className="rounded-xl p-4" style={{ backgroundColor: C.cremelc, border: `1px solid ${C.borda}` }}>
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <span className="px-3 py-1 rounded-full text-sm font-semibold" style={{ backgroundColor: '#E1F5EE', color: '#085041' }}>
                      {sanitize(t.polo_forte)}
                    </span>
                    <span className="text-gray-400 text-lg">↔</span>
                    <span className="px-3 py-1 rounded-full text-sm font-semibold" style={{ backgroundColor: '#FAECE7', color: '#712B13' }}>
                      {sanitize(t.polo_fragil)}
                    </span>
                  </div>
                  <p className="text-sm leading-relaxed text-gray-700">{sanitize(t.descricao)}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SEÇÃO 6 — O que esse perfil significa agora */}
        <div className="px-6 py-7 border-t" style={{ borderColor: C.borda }}>
          <p className="text-lg font-bold mb-1" style={{ color: C.escuro }}>O que esse perfil significa agora</p>
          {intencao && (
            <p className="text-sm mb-5 italic" style={{ color: C.cinza }}>
              Intenção declarada: {intencao}
            </p>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide mb-3" style={{ color: C.primario }}>
                O ativo que você carrega
              </p>
              <div className="space-y-2">
                {forcas.slice(0, 4).map((f, i) => (
                  <div key={`forca-s6-${i}`} className="rounded-lg px-4 py-3 text-sm" style={{ backgroundColor: '#E8F5EE', color: C.escuro }}>
                    <span className="font-semibold">{sanitize(f.habilidade)}</span>
                    {f.descricao && <span className="text-xs block mt-0.5" style={{ color: C.cinza }}>{sanitize(f.descricao)}</span>}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wide mb-3" style={{ color: C.coral }}>
                O que observar nessa fase
              </p>
              <div className="space-y-2">
                {desenvolvimento.slice(0, 4).map((d, i) => (
                  <div key={`dev-s6-${i}`} className="rounded-lg px-4 py-3 text-sm" style={{ backgroundColor: '#FAECE7', color: C.escuro }}>
                    <span className="font-semibold">{sanitize(d.habilidade)}</span>
                    {d.descricao && <span className="text-xs block mt-0.5" style={{ color: C.cinza }}>{sanitize(d.descricao)}</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* SEÇÃO 7 — Caminho de desenvolvimento */}
        {plano.length > 0 && (
          <div className="px-6 py-7 border-t" style={{ borderColor: C.borda }}>
            <p className="text-lg font-bold mb-5" style={{ color: C.escuro }}>Caminho de desenvolvimento proposto</p>
            <div className="space-y-3">
              {plano.map((foco, i) => (
                <FocoBlock
                  key={`foco-${i}`}
                  numero={i + 1}
                  titulo={sanitize(foco.titulo)}
                  habilidades={foco.habilidades || []}
                  descricao={foco.descricao}
                  headerColor={[C.primario, C.teal, C.roxo][i] || C.primario}
                />
              ))}
            </div>
          </div>
        )}

        {/* SEÇÃO 8 — Encerramento */}
        <div className="flex" style={{ backgroundColor: C.escuro }}>
          <div className="px-6 py-8 flex-1">
            <p className="text-xl font-bold text-white mb-2">O próximo passo começa aqui.</p>
            <p className="text-sm" style={{ color: C.menta }}>
              simby · plataforma de inteligência relacional
            </p>
          </div>
          <div className="px-6 py-8 flex flex-col justify-center gap-2" style={{ minWidth: 200 }}>
            <a href="https://www.simby.com.br" target="_blank" rel="noreferrer"
               className="text-sm hover:underline" style={{ color: C.menta }}>
              www.simby.com.br
            </a>
          </div>
        </div>

      </div>

      {/* CTA — só renderiza se onContinue for fornecido */}
      {onContinue && (
        <div className="mt-6 mb-24 md:mb-0 print:hidden">
          <Button
            onClick={onContinue}
            disabled={isSaving}
            size="lg"
            className="w-full text-white text-base py-6"
            style={{ backgroundColor: C.medio }}
          >
            {isSaving ? (
              <span className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                Salvando…
              </span>
            ) : (
              <>Começar minha jornada <ArrowRight className="w-5 h-5 ml-2" /></>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default Relatorio;
